import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "fecha";
import MUIDataTable from "mui-datatables";
import { makeStyles } from "@mui/styles";
import {
  Button,
  CircularProgress,
  Grid,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

import { convertDate, formatPrice } from "../../../utils/function";

import OrderDetail from "./OrderDetail";
import MultipleOrdersDeliveryDateSetModal from "../../Modal/MultipleOrdersDeliveryDateSetModal";

const useStyles = makeStyles({});

export default function OrdersTable({
  title,
  loading,
  items,
  hiddenColumns = [],
  expandable = true,
  handleDeliveryDateSet,
}) {
  const classes = useStyles();
  const [rowsExpanded, setRowsExpanded] = useState([]);
  const [data, setData] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [isDeliveryDateModalOpen, setIsDeliveryDateModalOpen] = useState(false);
  const [deliveryDateFormData, setDeliveryDateFormData] = useState({
    orders: [],
    deliveryDate: null,
  });

  const getUnitMaterialsTotalCost = (unitMaterials) => {
    let sum = 0;

    for (let i = 0; i < unitMaterials.length; i++) {
      const item = unitMaterials[i];
      sum += parseFloat(item.ITEM_COST) * parseInt(item.ITEM_QTY1);
    }

    return parseFloat(sum).toFixed(2);
  };

  useEffect(() => {
    if (items !== null) {
      const newData =
        items
          .sort((a, b) => a.PHASE_ID - b.PHASE_ID)
          .map((item) => ({ ...item, ACTIONS: null })) || [];
      let total = 0;

      items.forEach((item) => {
        const orderItems = item.ORDER_ITEMS ? JSON.parse(item.ORDER_ITEMS) : [];
        total += parseFloat(getUnitMaterialsTotalCost(orderItems));
      });

      setTotalCost(total);
      setData(newData);
    }
  }, [items]);

  const columns = [
    {
      name: "ORDER_ID",
      label: "PO",
      options: {
        display: !hiddenColumns.includes("ORDER_ID"),
      },
    },
    {
      name: "PO_TOTAL",
      label: "PO Total",
      options: {
        display: !hiddenColumns.includes("PO_TOTAL"),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const rowData = data[rowIndex];
          const orderItems = rowData.ORDER_ITEMS
            ? JSON.parse(rowData.ORDER_ITEMS)
            : [];

          return Object.keys(orderItems).length
            ? formatPrice(getUnitMaterialsTotalCost(orderItems || []))
            : "";
        },
      },
    },
    {
      name: "PHASE_ID",
      label: "Phase",
      options: {
        display: !hiddenColumns.includes("PHASE_ID"),
      },
    },
    {
      name: "LOAD_NUMBER",
      label: "Load",
      options: {
        display: !hiddenColumns.includes("LOAD_NUMBER"),
      },
    },
    {
      name: "",
      label: "Supplier",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const rowData = data[rowIndex];
          const orderItems = rowData.ORDER_ITEMS
            ? JSON.parse(rowData.ORDER_ITEMS)
            : [];
          const supplier =
            orderItems.length > 0
              ? orderItems[0].SUPPLIER_NAME || "Horizon"
              : "Horizon";

          return supplier || "";
        },
      },
    },
    {
      name: "FOREMAN",
      label: "Foreman",
      options: {
        display: !hiddenColumns.includes("FOREMAN"),
      },
    },
    {
      name: "ORDER_TIME",
      label: "Date Ordered",
      options: {
        display: !hiddenColumns.includes("ORDER_TIME"),
        customBodyRender: (value, tableMeta) => {
          return value ? convertDate(value) : "";
        },
      },
    },
    {
      name: "PO_CREATE_DATE",
      label: "Delivery Date",
      options: {
        display: !hiddenColumns.includes("PO_CREATE_DATE"),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const rowData = data[rowIndex];

          return value
            ? convertDate(value)
            : handleDeliveryDateSet && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => handleDeliveryDateModalOpen(e, rowData)}
                >
                  Set a date
                </Button>
              );
        },
      },
    },
    {
      name: "ACTIONS",
      label: "Actions",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const rowData = data[rowIndex];
          const orderItems = rowData.ORDER_ITEMS
            ? JSON.parse(rowData.ORDER_ITEMS)
            : [];
          const supplierId = orderItems.length
            ? orderItems[0].SUPPLIER_ID
            : null;

          return (
            <Link
              to={`/order/preview?oid=${rowData.ORDER_ID}&jid=${
                rowData.JOB_ID
              }&unit=${rowData.UNIT_ID}${
                supplierId ? `&sid=${supplierId}` : ""
              }`}
              target="_blank"
            >
              <Button
                size="small"
                variant="contained"
                onClick={(event) => event.stopPropagation()}
                fullWidth
              >
                View
              </Button>
            </Link>
          );
        },
      },
    },
  ];

  const options = {
    filter: true,
    filterType: "dropdown",
    responsive: "standard",
    selectableRows: "none",
    expandableRows: expandable,
    rowsExpanded: rowsExpanded,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    renderExpandableRow: (rowData, rowMeta) => {
      const colSpan = rowData.length + 1;
      const selectedOrderID = rowData[0];
      const selectedOrder = data.find(
        (item) => item.ORDER_ID === selectedOrderID
      );

      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <Grid container alignItems="center" spacing={4}>
              <Grid item xs={12}>
                <OrderDetail data={selectedOrder} />
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      );
    },
  };

  const handleRowExpandToggle = (rowData) => {
    const expandedRowIndex = rowsExpanded.indexOf(rowData.index);

    if (expandedRowIndex === -1) {
      // Row is currently not expanded, so add it to the rowsExpanded array
      setRowsExpanded([...rowsExpanded, rowData.index]);
    } else {
      // Row is currently expanded, so remove it from the rowsExpanded array
      setRowsExpanded(rowsExpanded.filter((index) => index !== rowData.index));
    }
  };

  const handleDeliveryDateModalOpen = (e, order) => {
    setIsDeliveryDateModalOpen(true);
    setDeliveryDateFormData({ ...deliveryDateFormData, orders: [order] });
  };
  const handleDeliveryDateModalClose = () => {
    setIsDeliveryDateModalOpen(false);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <MUIDataTable
          title={
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="h5">{title}</Typography>
              </Grid>
              {loading ? (
                <Grid item>
                  <CircularProgress size="1rem" />
                </Grid>
              ) : !hiddenColumns.includes("PO_TOTAL") ? (
                <Grid item>
                  <Typography variant="body1" color="grey">
                    (Total Cost: <b>{formatPrice(totalCost)}</b>)
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          }
          data={data}
          columns={columns}
          options={options}
          onRowExpansionChange={handleRowExpandToggle}
        />
      </Grid>
      <Grid item>
        <MultipleOrdersDeliveryDateSetModal
          open={isDeliveryDateModalOpen}
          loading={loading}
          orders={data}
          formData={deliveryDateFormData}
          handleSave={handleDeliveryDateSet}
          handleClose={handleDeliveryDateModalClose}
        />
      </Grid>
    </Grid>
  );
}
